<!--
// app/components/pages/region-tables-kwcc/TableKwccProbability.vue
-->
<script setup lang="ts">
import type { KwccTableData } from "~~/src/kwcc/domain/models/KwccTables";
import { setAliasUrl, slugify } from "~~/src/Shared/utils";
import type { Team } from "~~/src/team/domain/models/Team";
import type { TablesKwccData } from "~~/src/options/domain/models/CmsOptions";

type Props = {
  tableData: KwccTableData[] | TablesKwccData[];
  teamsObj?: Record<number, Team>;
  isHomeTable?: boolean;
  isInWorldCupTables?: boolean;
  isQueens?: boolean;
  noPoints?: boolean;
  extraClassifiedTeams?: number;
  isVictoriesDefeats?: boolean;
};

const props = defineProps<Props>();

const { $t, $localePath } = useI18n();

// Type guards to detect which data format we're using
const isKwccTableData = (item: any): item is KwccTableData => "team" in item;
const isTablesKwccData = (item: any): item is TablesKwccData => "team_id" in item;

// Helper function to get team ID from either data type
const getTeamId = (team: KwccTableData | TablesKwccData): number => {
  if (isKwccTableData(team)) {
    return team.teamId;
  }
  return team.team_id;
};

// Helper function to get team data from either teamsObj or tableData
const getTeamData = (team: KwccTableData | TablesKwccData): Team => {
  const teamId = getTeamId(team);

  // If teamsObj is provided and has data for this teamId, use it
  if (props.teamsObj && props.teamsObj[teamId]) {
    return props.teamsObj[teamId];
  }

  // For KwccTableData, we can use team directly
  if (isKwccTableData(team)) {
    return team.team;
  }

  // For TablesKwccData, teamsObj is required
  throw new Error("teamsObj is required for TablesKwccData format");
};

// Helper function to get rank/position
const getRank = (team: KwccTableData | TablesKwccData): number | undefined => {
  if (isKwccTableData(team)) {
    return team.rank;
  }
  return team.ranking;
};

// Helper function to check if team is classified/pre-qualified
const isClassified = (team: KwccTableData | TablesKwccData): boolean => {
  if (isKwccTableData(team)) {
    return team.isClassified;
  }
  return team.pre_qualified;
};

// Helper function to get classification reason
const getClassifiedReason = (team: KwccTableData | TablesKwccData): string | undefined => {
  if (isKwccTableData(team)) {
    return team.classifiedReason;
  }
  return team.reason;
};

// Helper function to get points
const getPoints = (team: KwccTableData | TablesKwccData): number => {
  if (isKwccTableData(team)) {
    return team.points;
  }
  // For TablesKwccData, we might need to sum pts_col_1 + pts_col_2 or use one of them
  // Assuming we want the total or most recent column
  return team.pts_col_2 || 0;
};

const getVictoriesDefeats = (team: KwccTableData | TablesKwccData): string | undefined => {
  if (isKwccTableData(team)) {
    return `${team.gameWon} - ${team.gameLost}`;
  }

  return undefined;
};

// Helper function to get probability
const getProbability = (team: KwccTableData | TablesKwccData): number | undefined => {
  if (isKwccTableData(team)) {
    return team.probability;
  }
  return team.probability;
};

const isExtraClassified = (team: KwccTableData | TablesKwccData): boolean => {
  if (!props.extraClassifiedTeams) return false;
  const nonClassifiedIndex = props.tableData.filter(t => !isClassified(t)).indexOf(team as any);
  return nonClassifiedIndex !== -1 && nonClassifiedIndex < props.extraClassifiedTeams;
};

const getTeamPath = (team: KwccTableData | TablesKwccData): string => {
  const teamData = getTeamData(team);
  return $localePath({
    name: "teams-team-teamName",
    params: {
      team: teamData.id || getTeamId(team),
      teamName: slugify(teamData.name),
    },
  });
};
</script>

<template>
  <div :class="[{ 'small-table w-full': isHomeTable }, { 'queens-table w-full': isQueens }]">
    <div
      v-if="tableData.length"
      id="standingDesktop"
      :class="[
        'bg-gray-900 p-2 rounded-lg my-3',
        { 'relative overflow-x-scroll hide-scrollbar': !isHomeTable },
      ]"
    >
      <div
        :class="[
          isHomeTable ? 'min-w-full' : 'overflow-x-scroll hide-scrollbar ',
          { '!max-w-min': isInWorldCupTables },
        ]"
      >
        <div class="header-table-standing">
          <div class="header-standing-team">
            <span class="text-left rtl:text-right">{{ $t("tables.position.ab") }}</span>
            <span class="col-start-2 col-span-3 ps-4">{{ $t("tables.team.word") }}</span>
          </div>
          <div
            :class="[
              'header-standing-data',
              (!tableData.some(t => getProbability(t)) && !noPoints) || isVictoriesDefeats
                ? 'grid-cols-1'
                : 'grid-cols-2',
            ]"
          >
            <span v-if="tableData.some(t => getProbability(t))" class="header-title-item">{{
              "prob. (%)"
            }}</span>
            <span v-if="!noPoints" class="header-title-item">{{ "pts" }}</span>
            <span v-if="isVictoriesDefeats" class="header-title-item">{{ "V - D" }}</span>
          </div>
        </div>
        <div
          v-for="(team, i) in tableData"
          :key="getTeamId(team)"
          :class="['standing-row relative', isClassified(team) ? 'w-full' : 'min-w-max']"
        >
          <div class="standing-row-left">
            <span
              :class="['split-standing', { '!bg-red-500': isClassified(team) || isExtraClassified(team) }]"
            ></span>
            <div class="standing-row-rank-team">
              <div v-if="!isClassified(team)" class="standing-rank">
                <p>{{ tableData.slice(0, i).filter(t => !isClassified(t)).length + 1 }}</p>
              </div>

              <div class="standing-team-container">
                <div class="logo-team-container">
                  <NuxtImg
                    :src="setAliasUrl(getTeamData(team)?.logo?.url)"
                    :alt="getTeamData(team)?.name"
                    class="object-contain"
                    :loading="i > 6 ? 'lazy' : 'eager'"
                    width="44"
                    height="44"
                  />
                </div>
                <div :class="{ 'hidden md:block': !isHomeTable }">
                  <NuxtLink :to="getTeamPath(team)">
                    <h2>
                      {{ isHomeTable ? getTeamData(team)?.shortName : getTeamData(team)?.name }}
                    </h2>
                  </NuxtLink>
                </div>
                <div v-if="!isHomeTable" class="block md:hidden">
                  <NuxtLink :to="getTeamPath(team)">
                    {{ getTeamData(team)?.shortName }}
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="!isClassified(team) || (isClassified(team) && isQueens)"
            :class="[
              'standing-row-data',
              (!tableData.some(t => getProbability(t)) && !noPoints) || isVictoriesDefeats
                ? 'grid-cols-1'
                : 'grid-cols-2',
            ]"
          >
            <span
              v-if="tableData.some(t => getProbability(t))"
              class="standing-table-item-data !text-white"
              >{{ getProbability(team) ?? "-" }}</span
            >
            <span v-if="!noPoints" class="standing-table-item-data">{{ getPoints(team) }}</span>
            <span v-if="isVictoriesDefeats" class="standing-table-item-data !text-sm">{{
              getVictoriesDefeats(team)
            }}</span>
          </div>
          <div
            v-else
            :class="[
              'standing-row-data',
              !tableData.some(t => getProbability(t)) || !noPoints ? 'grid-cols-1' : 'grid-cols-2',
            ]"
          >
            <span
              :class="[
                'standing-table-item-data !justify-start md:!justify-end uppercase !col-span-2 whitespace-normal break-words !h-auto py-1 !leading-tight !text-right',
                isHomeTable
                  ? 'min-w-1 !text-[10px] max-w-[150px]'
                  : '!min-w-[120px] !max-w-[240px] md:!text-base',
              ]"
            >
              {{ getClassifiedReason(team) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.header-table-standing {
  @apply flex justify-start items-center text-xs text-gray-400 uppercase mb-2 min-w-max;
}

.small-table .header-table-standing {
  @apply text-[10px];
}

.standing-row {
  @apply flex justify-start items-center relative bg-gray-800 h-16 mb-1 rounded-lg;
}

.small-table .standing-row {
  @apply !h-11;
}

.standing-row-left {
  @apply rounded-s-lg overflow-hidden bg-gray-800 sticky left-0 w-[170px] md:w-auto h-16 flex items-center;
}

.small-table .standing-row-left {
  @apply !w-auto max-w-min h-11 !static;
}

.standing-row-data {
  @apply rounded-e-lg overflow-hidden bg-gray-800 h-16;
}

.small-table .standing-row-data {
  @apply h-11;
}

.standing-row-rank-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 ps-3 md:ps-4 md:text-lg font-bold w-[160px] md:w-[362px] md:min-w-[362px] md:max-w-[450px];
}

.small-table .standing-row-rank-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[130px] md:w-[130px] md:min-w-[130px] md:max-w-max ps-1 md:ps-1 static text-sm;
}

.queens-table .standing-row-rank-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[160px] md:w-[270px] md:min-w-[270px] md:max-w-max ps-1 md:ps-1 static md:text-lg font-bold;
}

.header-standing-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 ps-3 md:ps-4 w-[170px] md:w-[362px] md:min-w-[362px] md:max-w-[450px] sticky left-0 bg-gray-900;
}

.small-table .header-standing-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[130px] md:w-[130px] md:min-w-[130px] md:max-w-max ps-1 md:ps-1 !static;
}

.queens-table .header-standing-team {
  @apply grid !grid-cols-[30px_1fr_1fr_1fr] w-[170px] md:w-[270px] md:min-w-[270px] md:max-w-max ps-1 md:ps-1 !static;
}

.standing-rank {
  @apply h-full w-full flex items-center justify-start text-left;
}

span.split-standing {
  @apply h-16 w-[4px] bg-gray-800 shrink-0;
}

.small-table span.split-standing {
  @apply h-11;
}

.standing-team-container {
  @apply col-start-2 col-span-3 text-left flex justify-start gap-3 md:gap-4 items-center h-full;
}

.small-table .standing-team-container {
  @apply max-w-min md:gap-2;
}

.logo-team-container {
  @apply w-11 h-11 flex justify-center items-center;
}

.small-table .logo-team-container {
  @apply !w-9 h-9;
}

.standing-table-item-data {
  @apply text-center h-full flex justify-center items-center md:text-lg text-gray-400 font-bold min-w-[60px];
}

.small-table .standing-table-item-data {
  @apply text-sm md:text-sm min-w-[48px];
}

.small-table .standing-table-item-data {
  @apply text-base;
}

.header-title-item {
  @apply justify-center flex;
}

.header-standing-data,
.standing-row-data {
  @apply grid gap-x-7 ms-auto text-center pe-5 min-w-[48px];
}

.small-table .header-standing-data,
.small-table .standing-row-data {
  @apply gap-x-2 ms-auto pe-2 min-w-[48px];
}
</style>
